import { hasPermission } from "../context/AuthContext";

/**
 * Cross-page workflow navigation presets (permission-gated at render time).
 * @typedef {{ to: string, label: string, permission?: string, description?: string }} WorkflowLink
 */

/** @type {Record<string, WorkflowLink[]>} */
export const WORKFLOW_PRESETS = {
  /** Gate → contract → sample → lab → weigh → purchase lifecycle */
  inboundProcurement: [
    { to: "/gate/procurement", permission: "page.gate", label: "Gate (Inward)" },
    { to: "/contracts", permission: "page.contracts", label: "Purchase contracts" },
    { to: "/stack-sample-intake", permission: "page.stack_samples", label: "Stack samples" },
    { to: "/stack-sample-approval", permission: "page.contracts", label: "Sample approval" },
    { to: "/lab/truck", permission: "page.lab", label: "Truck lab" },
    { to: "/weighbridge/inward", permission: "page.weighbridge", label: "Weighbridge" },
    { to: "/purchases", permission: "page.purchases", label: "Purchases" },
  ],
  postWeigh: [
    { to: "/lab/truck", permission: "page.lab", label: "Truck lab" },
    { to: "/purchases", permission: "page.purchases", label: "Purchases" },
    { to: "/unloading", permission: "page.unloading", label: "Unloading" },
    { to: "/purchase/vouchers", permission: "page.accounts", label: "Purchase vouchers" },
  ],
  quality: [
    { to: "/stack-sample-intake", permission: "page.stack_samples", label: "Sample intake" },
    { to: "/lab/stacks", permission: "page.lab", label: "Stack sample lab" },
    { to: "/stack-sample-approval", permission: "page.contracts", label: "Sample approval" },
    { to: "/contracts", permission: "page.contracts", label: "Contracts" },
  ],
  yardOps: [
    { to: "/gate/procurement", permission: "page.gate", label: "Gate (Inward)" },
    { to: "/weighbridge/inward", permission: "page.weighbridge", label: "Weighbridge" },
    { to: "/unloading", permission: "page.unloading", label: "Unloading" },
    { to: "/stacks", permission: "page.stacks", label: "Stacks" },
  ],
  outwardSupply: [
    { to: "/gate/outward", permission: "page.gate", label: "Gate (Outward)" },
    { to: "/sales-contracts", permission: "page.sales_contracts", label: "Sales Contract" },
    { to: "/sales/link", permission: "page.outward_dispatch", label: "Sales Link" },
    { to: "/sales/loading", permission: "page.outward_dispatch", label: "Loading" },
    { to: "/sales/vouchers", permission: "page.outward_dispatch", label: "Sales Voucher" },
    { to: "/sales/reports", permission: "page.sales_reports", label: "Sales Reporting" },
    { to: "/weighbridge/outward", permission: "page.weighbridge", label: "Weighbridge" },
    { to: "/accounts", permission: "page.accounts", label: "Accounts" },
  ],
  masterData: [
    { to: "/parties", permission: "page.parties", label: "Parties" },
    { to: "/contracts", permission: "page.contracts", label: "Contracts" },
  ],
};

/**
 * @param {import('../context/AuthContext').User | null} user
 * @param {WorkflowLink[]} links
 * @returns {WorkflowLink[]}
 */
export function filterWorkflowLinks(user, links) {
  if (!links?.length) return [];
  return links.filter((l) => !l.permission || hasPermission(user, l.permission));
}

/**
 * Build intake URL with optional party filter.
 * @param {number | string | null | undefined} partyId
 */
export function stackSampleIntakeUrl(partyId) {
  return partyId ? `/stack-sample-intake?party_id=${partyId}` : "/stack-sample-intake";
}

/**
 * Lab report page for a specific tab (truck | composite | stacks | plant).
 * @param {"truck"|"composite"|"stacks"|"plant"} [tab]
 */
export function labPageUrl(tab) {
  if (!tab || tab === "truck") return "/lab/truck";
  return `/lab/${tab}`;
}

/**
 * Purchases list — no native state filter in URL yet; link to list.
 */
export function purchasesUrl() {
  return "/purchases";
}
